import {
  FeatherBookOpen,
  FeatherLandmark,
  FeatherMessageSquare,
  FeatherReceipt,
  FeatherFileText,
} from "@subframe/core";
import type { NavIcon } from "../../../../layouts/v1";
import { HomeSummaryEntity } from "../../domain/entities/HomeSummaryEntity";
import { TASK_TARGETS, TaskTarget } from "../navigation/taskTargets";

/**
 * One actionable line inside a task group.
 *
 * A row is either *actionable* (count > 0, shows the call to action) or *done*
 * (count is 0, shows the reassuring "nothing here" line). Both states are always
 * rendered, so the page reads as a complete checklist rather than collapsing to
 * nothing on a quiet day.
 */
export interface TaskRowView {
  id: string;
  /** Text to render, already resolved for the current count. */
  label: string;
  count: number;
  done: boolean;
  target: TaskTarget;
}

export interface TaskGroupView {
  id: string;
  title: string;
  icon: NavIcon;
  rows: TaskRowView[];
}

/** "3 invoices" / "1 invoice" */
const plural = (count: number, singular: string, pluralForm?: string): string =>
  `${count} ${count === 1 ? singular : pluralForm ?? `${singular}s`}`;

interface RowSpec {
  id: string;
  count: number;
  /** Rendered when count > 0. Receives the pluralised noun phrase. */
  action: (noun: string) => string;
  /** Rendered when count is 0. */
  resolved: string;
  singular: string;
  pluralForm?: string;
  target: TaskTarget;
}

const buildRow = (spec: RowSpec): TaskRowView => {
  const done = spec.count === 0;

  return {
    id: spec.id,
    label: done
      ? spec.resolved
      : spec.action(plural(spec.count, spec.singular, spec.pluralForm)),
    count: spec.count,
    done,
    target: spec.target,
  };
};

/**
 * Turns the API summary into the checklist the page renders.
 *
 * Kept out of the component so the copy and the count-to-label rules live in one
 * readable place, and so the page itself stays presentational.
 */
export const buildTaskGroups = (summary: HomeSummaryEntity): TaskGroupView[] => {
  const { invoices, bills, bank, queries, journals } = summary.groups;

  return [
    {
      id: "invoices",
      title: "Invoices",
      icon: FeatherReceipt,
      rows: [
        buildRow({
          id: "invoices-on-hold",
          count: invoices.onHold,
          singular: "invoice",
          action: (noun) => `Follow up on ${noun} on hold`,
          resolved: "No invoices on hold",
          target: TASK_TARGETS.invoicesOnHold,
        }),
        buildRow({
          id: "invoices-under-review",
          count: invoices.underReview,
          singular: "invoice",
          action: (noun) => `Check ${noun} under review`,
          resolved: "No invoices under review",
          target: TASK_TARGETS.invoicesUnderReview,
        }),
        buildRow({
          id: "invoices-to-review",
          count: invoices.toReview,
          singular: "invoice",
          action: (noun) => `Review ${noun}`,
          resolved: "No invoices to review right now",
          target: TASK_TARGETS.invoicesToReview,
        }),
      ],
    },
    {
      id: "bills",
      title: "Bills",
      icon: FeatherFileText,
      rows: [
        buildRow({
          id: "bills-on-hold",
          count: bills.onHold,
          singular: "bill",
          action: (noun) => `Follow up on ${noun} on hold`,
          resolved: "No bills on hold",
          target: TASK_TARGETS.billsOnHold,
        }),
        buildRow({
          id: "bills-under-review",
          count: bills.underReview,
          singular: "bill",
          action: (noun) => `Resolve ${noun} under review`,
          resolved: "No bills under review",
          target: TASK_TARGETS.billsUnderReview,
        }),
        buildRow({
          id: "bills-to-review",
          count: bills.toReview,
          singular: "bill",
          action: (noun) => `Review ${noun}`,
          resolved: "No bills to review right now",
          target: TASK_TARGETS.billsToReview,
        }),
      ],
    },
    {
      id: "bank",
      title: "Bank",
      icon: FeatherLandmark,
      rows: [
        buildRow({
          id: "bank-to-categorize",
          count: bank.toCategorize,
          singular: "bank transaction",
          action: (noun) => `Categorize ${noun}`,
          resolved: "All bank transactions categorized",
          target: TASK_TARGETS.bankToCategorize,
        }),
        buildRow({
          id: "bank-on-hold",
          count: bank.onHold,
          singular: "transaction",
          action: (noun) => `Follow up on ${noun} on hold`,
          resolved: "No transactions on hold",
          target: TASK_TARGETS.bankOnHold,
        }),
        buildRow({
          id: "bank-flagged",
          count: bank.flagged,
          singular: "flagged transaction",
          action: (noun) => `Review ${noun}`,
          resolved: "No flagged transactions",
          target: TASK_TARGETS.bankFlagged,
        }),
      ],
    },
    {
      id: "queries",
      title: "Queries",
      icon: FeatherMessageSquare,
      rows: [
        buildRow({
          id: "queries-assigned",
          count: queries.openAssignedToMe,
          singular: "open query",
          pluralForm: "open queries",
          action: (noun) => `Address ${noun} raised to you`,
          resolved: "No open queries raised to you",
          target: TASK_TARGETS.queriesAssignedToMe,
        }),
        buildRow({
          id: "queries-client",
          count: queries.pendingClientReplies,
          singular: "client reply",
          pluralForm: "client replies",
          action: (noun) => `Chase ${noun}`,
          resolved: "No pending client replies",
          target: TASK_TARGETS.queriesPendingClient,
        }),
        buildRow({
          id: "queries-senior",
          count: queries.pendingSeniorReplies,
          singular: "senior accountant reply",
          pluralForm: "senior accountant replies",
          action: (noun) => `Chase ${noun}`,
          resolved: "No pending senior accountant replies",
          target: TASK_TARGETS.queriesPendingSenior,
        }),
      ],
    },
    {
      id: "journals",
      title: "Journals",
      icon: FeatherBookOpen,
      rows: [
        buildRow({
          id: "journals-approve",
          count: journals.pendingMyApproval,
          singular: "journal entry",
          pluralForm: "journal entries",
          action: (noun) => `Approve ${noun}`,
          resolved: "No journal entries awaiting your approval",
          target: TASK_TARGETS.journalsPendingApproval,
        }),
        buildRow({
          id: "journals-recurring",
          count: journals.activeRecurringTemplates,
          singular: "recurring journal schedule",
          action: () => "Review recurring journal schedules",
          resolved: "No recurring journal schedules",
          target: TASK_TARGETS.journalsRecurring,
        }),
      ],
    },
  ];
};
